/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import DownloadIcon from '@mui/icons-material/Download';
import {
  alpha,
  Box,
  type BoxProps,
  Chip,
  Collapse,
  Link as MLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { type Config, type FileExt, format, getConfigPath } from '@poweralpha/common';
import { range, uniqueId } from 'lodash';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { highlightBackground } from '../styles';
import { ExpandIconButton } from './buttons';
import { useFileDownload } from './download/useFileDownload';
import { HtmlTooltip } from './HtmlTooltip';
import { InfoPill } from './text';

interface TableCellProps {
  values: Array<number | React.ReactNode | undefined | null>;
  bold?: boolean;
  italics?: boolean;
  alignCenterIdx?: Array<number>;
  alignRightIdx?: Array<number>;
  indent?: number;
  colSpan?: number;
}

export const TableCells = ({
  values,
  bold = false,
  italics = false,
  alignCenterIdx = [],
  alignRightIdx = [],
  indent = 0,
  colSpan = 1,
}: TableCellProps) => {
  const fontWeight = bold ? 'fontWeightBold' : 'fontWeightRegular';
  const rows = values.map((value, i) => {
    let alignment: 'left' | 'right' | 'center';
    if (alignCenterIdx.includes(i)) {
      alignment = 'center';
    } else if (alignRightIdx.includes(i)) {
      alignment = 'right';
    } else {
      alignment = 'left';
    }
    return (
      <TableCell key={uniqueId(value?.toString() ?? undefined)} align={alignment} colSpan={colSpan}>
        <Box sx={{ fontWeight, paddingLeft: indent }}>{italics ? <em>{value}</em> : value}</Box>
      </TableCell>
    );
  });

  return <>{rows}</>;
};

interface TwoColumnInfoTableProps {
  values: Array<number | React.ReactNode | undefined | null>;
  labels?: Array<React.ReactNode>;
  boldRowIdx?: Array<number>;
  italicsRowIdx?: Array<number>;
  indentRowIdx?: Array<number>;
  indentValue?: number;
  units?: Array<string>;
  alignValues?: 'right' | 'left';
}

export const TwoColumnInfoTable = ({
  values,
  labels = [],
  boldRowIdx = [],
  italicsRowIdx = [],
  indentRowIdx = [],
  indentValue = 0,
  units = [],
  alignValues = 'right',
}: TwoColumnInfoTableProps) => {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: refactor at some point
  const rows = values.map((value, i) => {
    const fontWeight = boldRowIdx.includes(i) ? 'fontWeightBold' : 'fontWeightRegular';

    let indent: number | undefined = 0;
    if (indentRowIdx.length > 0) {
      // Case 1: indentRowIdx is not empty
      indent = indentRowIdx.includes(i) ? indentValue : 0;
    } else if (indentRowIdx.length === 0 && indentValue !== 0) {
      // Case 2: indentRowIdx is empty, but indentValue is not zero
      indent = indentValue;
    }

    const key = `row-${i}`;

    return (
      <TableRow key={key}>
        <TableCell>
          <Box sx={{ fontWeight, paddingLeft: indent }}>
            {italicsRowIdx.includes(i) ? <em>{labels[i]}</em> : labels[i]}
          </Box>
        </TableCell>
        <TableCell align={alignValues}>
          <Box sx={{ fontWeight }}>{italicsRowIdx.includes(i) ? <em>{value}</em> : value}</Box>
        </TableCell>
        {units && units.length > 0 && (
          <TableCell>
            <Box sx={{ fontWeight }}>
              {italicsRowIdx.includes(i) ? <em>{units[i]}</em> : units[i]}
            </Box>
          </TableCell>
        )}
      </TableRow>
    );
  });

  return <>{rows}</>;
};

interface ArchivedTableRowProps {
  archived: boolean | undefined;
}

export const ArchivedTableRow = ({ archived }: ArchivedTableRowProps) => {
  const theme = useTheme();
  const backgroundColor = archived ? alpha(theme.palette.warning.main, 0.7) : undefined;

  return archived !== undefined ? (
    <TableRow>
      <TableCell sx={{ backgroundColor }}>Status</TableCell>
      <TableCell align="right" sx={{ backgroundColor }}>
        <Typography
          sx={{
            color: archived ? undefined : 'success.main',
            fontSize: 14,
            fontWeight: archived ? 'fontWeightBold' : 'fontWeightRegular',
          }}
        >
          {archived ? 'Archived' : 'Current'}
        </Typography>
      </TableCell>
    </TableRow>
  ) : (
    <TableRow />
  );
};

interface ConfigTableRowProps {
  type: 'fimo' | 'pvsim' | 'pvcosting' | 'esscosting' | 'plantsim';
  config: Config | undefined | null;
  alignRight?: boolean;
}

export const ConfigTableCell = ({ type, config, alignRight = false }: ConfigTableRowProps) => (
  <TableCell align={alignRight ? 'right' : 'left'}>
    {config && (
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: alignRight ? 'flex-end' : 'space-between' }}
      >
        <MLink component={Link} to={getConfigPath(type, config)}>
          {config?.config_name?.split('_')?.join(' ')}
        </MLink>
        <InfoPill label={`v${config?.version}`} />
      </Stack>
    )}
    {config?.comment && (
      <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
        {config.comment.length > 50 ? `${config.comment.slice(0, 50)}...` : config.comment}
      </Typography>
    )}
  </TableCell>
);

export const LoadingTable = () => (
  <TableContainer component={Paper}>
    <Table size="small">
      <TableBody>
        {range(3).map((i) => (
          <TableRow key={i}>
            <TableCell>
              <Skeleton />
            </TableCell>
            <TableCell align="right">
              <Skeleton />
            </TableCell>
            <TableCell align="right">
              <Skeleton />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export const SectionRow = ({
  label,
  colSpan = 2,
  indent = 0,
}: {
  label: string;
  indent?: number;
  colSpan?: number;
}) => (
  <TableRow>
    <TableCells values={[label]} italics colSpan={colSpan} indent={indent} />
  </TableRow>
);

export const SectionRowWithValueLabel = ({
  label,
  valueLabel,
  indent = 0,
}: {
  label: string;
  valueLabel: string;
  indent?: number;
}) => (
  <TableRow>
    <TableCells values={[label, valueLabel, '']} italics indent={indent} alignRightIdx={[1]} />
  </TableRow>
);

export interface FormattedValueProps {
  isLoading: boolean | undefined;
  value: number | string | null | undefined;
  formatPrecision?: number;
  formatScale?: number;
  formatDefaultValue?: number;
  percent?: boolean;
}

export const FormattedValue = ({
  isLoading,
  value,
  formatPrecision,
  formatScale,
  formatDefaultValue,
  percent,
}: FormattedValueProps) => {
  if (isLoading) {
    return <Skeleton />;
  }
  return (
    <span>
      {typeof value === 'string'
        ? value
        : format(value, formatPrecision, formatScale, formatDefaultValue, false, false, percent)}
    </span>
  );
};

interface Download {
  link: string;
  fileName: string;
  fileExt?: FileExt;
}

export interface FormattedTableRowProps<V extends string | number | React.ReactElement> {
  label: string;
  value?: V | null;
  formatPrecision?: number;
  formatScale?: number;
  formatDefaultValue?: number;
  isLoading?: boolean;
  messageFn?: (value: V | null | undefined) => string | null;
  type?: 'notImplemented' | 'intermediateResults' | 'dynamic';
  percent?: boolean;
  link?: string;
  defaultPlaceholder?: string;
  download?: Download;
}

export function FormattedTableRow<V extends string | number | React.ReactElement>({
  label,
  value,
  formatPrecision,
  formatScale,
  formatDefaultValue,
  isLoading,
  messageFn,
  type,
  percent,
  link,
  defaultPlaceholder,
  download,
}: Readonly<FormattedTableRowProps<V>>) {
  const generateId = (x: string) => x.replace(/\s/g, '').toLowerCase();

  const fontStyle = (isLabel = false): string => {
    if (type === 'notImplemented' || (type === 'dynamic' && !isLabel)) {
      return 'italic';
    }
    return 'regular';
  };

  const fontWeight = (): string => {
    if (type === 'notImplemented') {
      return 'fontWeightLight';
    }
    if (type === 'intermediateResults') {
      return 'fontWeightBold';
    }
    return 'fontWeightRegular';
  };

  const message = messageFn?.(value) ? <>Tip: {messageFn(value)}</> : null;

  const formats = () => {
    if (defaultPlaceholder && value == null) {
      return defaultPlaceholder;
    }
    if (value != null && typeof value === 'object' && 'type' in value) {
      return value;
    }

    return (
      <FormattedValue
        isLoading={isLoading}
        value={value}
        formatPrecision={formatPrecision}
        formatScale={formatScale}
        formatDefaultValue={formatDefaultValue}
        percent={percent}
      />
    );
  };

  const downloadCustomLossesFile = useFileDownload(download?.link ?? '', {
    baseFileName: download?.fileName ?? '',
    ext: download?.fileExt ?? 'csv',
  });

  const content = () => {
    if (download) {
      return (
        <Chip
          icon={<DownloadIcon />}
          disabled={downloadCustomLossesFile.isDownloading}
          label={value ?? 'File'}
          onClick={downloadCustomLossesFile.startDownload}
        />
      );
    }

    if (link) {
      return (
        <MLink component={Link} to={link}>
          {formats()}
        </MLink>
      );
    }
    return formats();
  };

  return (
    <HtmlTooltip title={message}>
      <TableRow key={generateId(label)}>
        <TableCell>
          <Box sx={{ fontStyle: fontStyle(true), fontWeight: fontWeight() }}>{label}</Box>
        </TableCell>
        <TableCell align="right">
          <Box
            sx={(t) => ({
              fontStyle: fontStyle(),
              fontWeight: fontWeight(),
              color: message
                ? highlightBackground(t, { color: 'warning', coefficient: 0.3 })
                : undefined,
            })}
          >
            {content()}
          </Box>
        </TableCell>
      </TableRow>
    </HtmlTooltip>
  );
}

interface CollapsableTableRowProps<T> {
  label: React.ReactNode;
  collapsedRows: Array<Array<T>> | React.ReactNode;
  rowRender?: (row: Array<T>, rowIndex: number) => React.ReactElement;
  restOfRow?: React.ReactElement;
  labelProps?: BoxProps;
  colgroup?: React.ReactElement;
  colSpan?: number;
  collapsedRowsColSpan?: number;
  initiallyCollapsed?: boolean;
}

export function CollapsableTableRow<T>({
  label,
  collapsedRows,
  rowRender,
  restOfRow,
  labelProps,
  colgroup,
  colSpan,
  collapsedRowsColSpan,
  initiallyCollapsed,
}: Readonly<CollapsableTableRowProps<T>>) {
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed ?? false);

  return (
    <>
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ pl: 1, pr: 1 }}>
          <Stack direction="row" spacing={1}>
            <ExpandIconButton
              collapsed={isCollapsed}
              onClick={() => setIsCollapsed((prev) => !prev)}
              style={{ opacity: undefined }}
              sx={{ height: 20, width: 20 }}
            />
            <Box {...labelProps} sx={{ width: '100%', ...(labelProps?.sx ?? {}) }}>
              {label}
            </Box>
          </Stack>
        </TableCell>
        {restOfRow}
      </TableRow>
      <TableRow sx={{ '& > td': { borderBottom: 'unset' } }}>
        <TableCell style={{ padding: 0 }} colSpan={collapsedRowsColSpan}>
          <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
            <Table size="small" style={{ tableLayout: 'fixed' }}>
              {colgroup}
              <TableBody>
                {Array.isArray(collapsedRows)
                  ? collapsedRows.map((row, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: fine here
                      <TableRow key={`${row[0]}-${i}`} sx={{ '& > th': { padding: 0, border: 0 } }}>
                        {rowRender?.(row, i) ??
                          row.map((cell, j) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: probably fine
                            <TableCell key={`${cell}-${j}`} sx={{ ml: j === 0 ? 1 : 0 }}>
                              {cell as React.ReactNode}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))
                  : collapsedRows}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
